import { useMemo, useState } from "react";
import {
  api,
  type EngagementParts,
  type RaidEntry,
  type RaidKind,
  type TeamMember,
} from "../lib/api";
import { useAction, useResource } from "../lib/useResource";
import {
  Button,
  Empty,
  Failed,
  Field,
  Label,
  Mark,
  Select,
  SkeletonRows,
  TextArea,
} from "../components/primitives";
import { day, stamp } from "../lib/format";

/**
 * The RAID log — what could knock this engagement over.
 *
 * Four kinds, and the distinction between them is the value. A register
 * that blurs them becomes a list nobody acts on:
 *
 *   Risk         might happen, would hurt. Carries likelihood and impact.
 *   Assumption   treated as true without proof, because the work cannot
 *                start otherwise. Carries the date it must be confirmed by.
 *   Issue        has happened. Carries an owner and a resolution date.
 *   Dependency   owed by somebody else, by a date. Tracked apart because it
 *                is the commonest cause of slippage and usually not yours
 *                to fix.
 *
 * The sharpest line is **risk → issue**. "It happened" closes the risk and
 * opens the issue it became, in one transaction, with both rows kept — so
 * the register shows the transition rather than quietly swapping one row
 * for another. That is what makes it a record instead of a worry list.
 *
 * Raising is open to anyone on the engagement: the person doing the work
 * sees the risk first, and a log only a Lead may write to learns things
 * late. Curating is a Lead's, or the entry owner's — `mayCurate` is the
 * server's answer, per entry.
 */

const KINDS: Array<{ value: RaidKind; label: string; plural: string; hint: string }> = [
  {
    value: "risk",
    label: "Risk",
    plural: "Risks",
    hint: "Might happen, would hurt. Say the mitigation in the detail.",
  },
  {
    value: "assumption",
    label: "Assumption",
    plural: "Assumptions",
    hint: "Treated as true without proof. Say what changes if it is false.",
  },
  {
    value: "issue",
    label: "Issue",
    plural: "Issues",
    hint: "Already true. Needs an owner and a date to be resolved by.",
  },
  {
    value: "dependency",
    label: "Dependency",
    plural: "Dependencies",
    hint: "Owed by someone else. Say who, and by when.",
  },
];

const GRADE_TONE: Record<string, "rest" | "warning" | "danger"> = {
  low: "rest",
  medium: "warning",
  high: "danger",
};

export function RaidRegister({
  engagementId,
  parts,
  assignable,
}: {
  engagementId: string;
  parts?: EngagementParts;
  assignable: TeamMember[];
}) {
  const log = useResource<RaidEntry[]>(
    () => api.get<RaidEntry[]>(`/api/engagements/${engagementId}/raid`),
    [engagementId],
  );

  const [raising, setRaising] = useState<RaidKind | null>(null);
  const [showClosed, setShowClosed] = useState(false);

  const entries = log.resource.state === "ready" ? log.resource.data : [];
  const open = useMemo(() => entries.filter((e) => e.status === "open"), [entries]);
  const closed = useMemo(
    () => entries.filter((e) => e.status === "closed"),
    [entries],
  );
  const overdue = open.filter((e) => e.overdue).length;

  return (
    <section className="mt-14 border-t border-line-strong pt-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <Label as="h2">The register</Label>
          <p className="mt-1 max-w-[62ch] text-sm text-secondary">
            Risks, assumptions, issues and dependencies. The dated record of
            what was flagged and when.
          </p>
        </div>
        <p className="text-sm text-secondary">
          {open.length} open
          {overdue > 0 && <span className="text-danger"> · {overdue} overdue</span>}
        </p>
      </div>

      {log.resource.state === "loading" && <SkeletonRows rows={3} />}
      {log.resource.state === "failed" && (
        <Failed
          message={log.resource.message}
          correlationId={log.resource.correlationId}
        />
      )}

      {log.resource.state === "ready" && (
        <>
          <div className="mt-4 flex flex-wrap gap-3">
            {KINDS.map((k) => (
              <Button
                key={k.value}
                tone={raising === k.value ? "secondary" : "quiet"}
                onClick={() =>
                  setRaising((cur) => (cur === k.value ? null : k.value))
                }
              >
                {raising === k.value ? "Cancel" : `Raise a ${k.label.toLowerCase()}`}
              </Button>
            ))}
          </div>

          {raising && (
            <Raise
              engagementId={engagementId}
              kind={raising}
              parts={parts}
              assignable={assignable}
              onDone={() => {
                setRaising(null);
                log.reload();
              }}
            />
          )}

          {open.length === 0 && (
            <Empty title="Nothing on the register">
              Populate it at kickoff: what might go wrong, what you are
              assuming, what is already a problem, and what you need from
              someone else.
            </Empty>
          )}

          {KINDS.map((k) => {
            const rows = open.filter((e) => e.kind === k.value);
            if (rows.length === 0) return null;
            return (
              <div key={k.value} className="mt-8">
                <h3 className="label">
                  {k.plural} <span className="text-muted">({rows.length})</span>
                </h3>
                {rows.map((e) => (
                  <Entry
                    key={e.id}
                    entry={e}
                    assignable={assignable}
                    onChanged={log.reload}
                  />
                ))}
              </div>
            );
          })}

          {closed.length > 0 && (
            <div className="mt-10">
              <button
                type="button"
                onClick={() => setShowClosed((v) => !v)}
                aria-expanded={showClosed}
                className="label text-secondary transition-colors hover:text-ink"
              >
                <span
                  aria-hidden
                  className={
                    "mr-2 inline-block font-mono transition-transform duration-150 " +
                    (showClosed ? "rotate-90" : "")
                  }
                >
                  {"›"}
                </span>
                Closed ({closed.length})
              </button>
              {/* Closed entries are the point of keeping a register, so they
                  are collapsed rather than dropped. What you flagged and
                  when is the thing you reach for months later. */}
              <div hidden={!showClosed}>
                {closed.map((e) => (
                  <Entry
                    key={e.id}
                    entry={e}
                    assignable={assignable}
                    onChanged={log.reload}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Entry({
  entry: e,
  assignable,
  onChanged,
}: {
  entry: RaidEntry;
  assignable: TeamMember[];
  onChanged: () => void;
}) {
  const [panel, setPanel] = useState<"close" | "promote" | null>(null);
  const closed = e.status === "closed";

  return (
    <div className="border-b border-line py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className={`text-base ${closed ? "text-secondary" : "text-ink"}`}>
            {e.title}
          </p>
          {e.detail && (
            <p className="mt-1 max-w-[62ch] whitespace-pre-line text-sm text-secondary">
              {e.detail}
            </p>
          )}
          <p className="mt-1 text-sm text-secondary">
            {e.workstream && <span>{e.workstream} · </span>}
            {e.owedBy ? `owed by ${e.owedBy}` : (e.owner ?? "nobody owns it")}
            {e.dueOn && (
              <span className={e.overdue ? "text-danger" : undefined}>
                {" · "}
                {e.overdue ? "overdue " : "by "}
                {day(e.dueOn)}
              </span>
            )}
            <span className="text-muted">
              {" · raised by "}
              {e.raisedBy ?? "Someone no longer listed"} {stamp(e.raisedAt)}
            </span>
          </p>
          {closed && e.closedNote && (
            <p className="mt-1 max-w-[62ch] border-l-2 border-line pl-3 text-sm text-secondary">
              {e.becameId ? "Became an issue: " : "Closed: "}
              {e.closedNote}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 text-sm">
          {/* Two grades shown rather than one multiplied score. Multiplying
              two three-point scales makes a number that looks like
              measurement and is not one. */}
          {e.likelihood && (
            <Mark tone={GRADE_TONE[e.likelihood]}>{e.likelihood} likelihood</Mark>
          )}
          {e.impact && <Mark tone={GRADE_TONE[e.impact]}>{e.impact} impact</Mark>}

          {!closed && e.mayCurate && e.kind === "risk" && (
            <Button
              tone="quiet"
              onClick={() => setPanel((p) => (p === "promote" ? null : "promote"))}
            >
              It happened
            </Button>
          )}
          {!closed && e.mayCurate && (
            <Button
              tone="quiet"
              onClick={() => setPanel((p) => (p === "close" ? null : "close"))}
            >
              Close
            </Button>
          )}
        </div>
      </div>

      {panel && (
        <Resolve
          entryId={e.id}
          mode={panel}
          onDone={() => {
            setPanel(null);
            onChanged();
          }}
          onCancel={() => setPanel(null)}
        />
      )}

      {/* Reassigning an owner is the one edit worth having inline; the rest
          of the detail is written when it is raised. */}
      {!closed && e.mayCurate && (
        <Reassign entry={e} assignable={assignable} onChanged={onChanged} />
      )}
    </div>
  );
}

function Resolve({
  entryId,
  mode,
  onDone,
  onCancel,
}: {
  entryId: string;
  mode: "close" | "promote";
  onDone: () => void;
  onCancel: () => void;
}) {
  const [note, setNote] = useState("");

  const act = useAction(async () => {
    await api.post(`/api/raid/${entryId}/${mode}`, { note: note.trim() });
    onDone();
  });

  return (
    <div className="mt-3 grid max-w-2xl gap-3">
      <TextArea
        label={mode === "promote" ? "What happened" : "Why it is closed"}
        hint={
          mode === "promote"
            ? "This closes the risk and opens the issue it became. Both stay on the register."
            : "Required. “Closed” on its own is the least useful thing a register can say later."
        }
        value={note}
        onChange={(ev) => setNote(ev.target.value)}
      />
      <div className="flex gap-3">
        <Button
          tone={mode === "promote" ? "danger" : "primary"}
          loading={act.pending}
          disabled={!note.trim()}
          onClick={() => void act.invoke()}
        >
          {mode === "promote" ? "It happened — open the issue" : "Close it"}
        </Button>
        <Button tone="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {act.refusal && (
        <p className="border-l-2 border-danger pl-4 text-sm text-ink">
          {act.refusal.message}
        </p>
      )}
    </div>
  );
}

function Reassign({
  entry: e,
  assignable,
  onChanged,
}: {
  entry: RaidEntry;
  assignable: TeamMember[];
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [owner, setOwner] = useState(e.ownerUserId ?? "");

  const save = useAction(async () => {
    await api.patch(`/api/raid/${e.id}`, { ownerUserId: owner || null });
    setOpen(false);
    onChanged();
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2 text-sm text-muted underline-offset-4 hover:text-ink hover:underline"
      >
        {e.owner ? "Reassign" : "Give it an owner"}
      </button>
    );
  }

  return (
    <div className="mt-3 flex max-w-xl flex-wrap items-end gap-3">
      <Select label="Owner" value={owner} onChange={(ev) => setOwner(ev.target.value)}>
        <option value="">Nobody</option>
        {assignable.map((m) => (
          <option key={`${m.userId}-${m.assignmentId ?? ""}`} value={m.userId}>
            {m.fullName}
          </option>
        ))}
      </Select>
      <Button tone="primary" loading={save.pending} onClick={() => void save.invoke()}>
        Save
      </Button>
      <Button tone="quiet" onClick={() => setOpen(false)}>
        Cancel
      </Button>
      {save.refusal && (
        <p className="w-full border-l-2 border-danger pl-4 text-sm text-ink">
          {save.refusal.message}
        </p>
      )}
    </div>
  );
}

function Raise({
  engagementId,
  kind,
  parts,
  assignable,
  onDone,
}: {
  engagementId: string;
  kind: RaidKind;
  parts?: EngagementParts;
  assignable: TeamMember[];
  onDone: () => void;
}) {
  const [title, setTitle] = useState("");
  const [detail, setDetail] = useState("");
  const [workstream, setWorkstream] = useState("");
  const [owner, setOwner] = useState("");
  const [likelihood, setLikelihood] = useState("medium");
  const [impact, setImpact] = useState("medium");
  const [owedBy, setOwedBy] = useState("");
  const [dueOn, setDueOn] = useState("");

  const spec = KINDS.find((k) => k.value === kind)!;

  const raise = useAction(async () => {
    await api.post(`/api/engagements/${engagementId}/raid`, {
      kind,
      title: title.trim(),
      detail: detail.trim() || undefined,
      workstreamId: workstream || undefined,
      ownerUserId: owner || undefined,
      // Sent only where they mean something. The database refuses the rest
      // with a CHECK, but a request that cannot be right is not worth
      // making.
      ...(kind === "risk" ? { likelihood, impact } : {}),
      ...(kind === "dependency" && owedBy.trim()
        ? { owedBy: owedBy.trim() }
        : {}),
      dueOn: dueOn || undefined,
    });
    onDone();
  });

  return (
    <div className="mt-4 grid max-w-3xl gap-4 border-l-2 border-line pl-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <Field
          label={spec.label}
          hint={spec.hint}
          maxLength={300}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <div className="sm:col-span-2">
        <TextArea
          label={
            kind === "risk"
              ? "Mitigation"
              : kind === "assumption"
                ? "What changes if it is false"
                : "Detail"
          }
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
        />
      </div>

      {kind === "risk" && (
        <>
          <Select
            label="Likelihood"
            value={likelihood}
            onChange={(e) => setLikelihood(e.target.value)}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </Select>
          <Select
            label="Impact"
            value={impact}
            onChange={(e) => setImpact(e.target.value)}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </Select>
        </>
      )}

      {kind === "dependency" && (
        <div className="sm:col-span-2">
          <Field
            label="Owed by"
            hint="Often not someone on this platform — a client team, a third party."
            value={owedBy}
            onChange={(e) => setOwedBy(e.target.value)}
          />
        </div>
      )}

      <Select
        label="Workstream"
        hint="Optional. Tagging it shows which stream carries the most."
        value={workstream}
        onChange={(e) => setWorkstream(e.target.value)}
      >
        <option value="">The engagement as a whole</option>
        {(parts?.workstreams ?? []).map((w) => (
          <option key={w.id} value={w.id}>
            {w.name}
          </option>
        ))}
      </Select>

      <Field
        label={
          kind === "assumption"
            ? "Confirm by"
            : kind === "dependency"
              ? "Needed by"
              : kind === "issue"
                ? "Resolve by"
                : "Bites by"
        }
        type="date"
        value={dueOn}
        onChange={(e) => setDueOn(e.target.value)}
      />

      <Select label="Owner" value={owner} onChange={(e) => setOwner(e.target.value)}>
        <option value="">Nobody yet</option>
        {assignable.map((m) => (
          <option key={`${m.userId}-${m.assignmentId ?? ""}`} value={m.userId}>
            {m.fullName}
          </option>
        ))}
      </Select>

      <div className="flex items-end gap-3">
        <Button
          tone="primary"
          loading={raise.pending}
          disabled={!title.trim()}
          onClick={() => void raise.invoke()}
        >
          Raise it
        </Button>
      </div>

      {raise.refusal && (
        <p className="border-l-2 border-danger pl-4 text-sm text-ink sm:col-span-2">
          {raise.refusal.message}
        </p>
      )}
    </div>
  );
}
